import type { FC } from 'react';
import React, { useContext, useCallback, useMemo } from 'react';
import { styled } from '@compiled/react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import type { SearchCSS } from '@atlaskit/atlassian-navigation';

import { ProductSearchInputSkeletonInteractiveSSR } from '@atlassian/product-search-dialog/skeleton';

import { useRenderServerPlaceholder } from '@confluence/ssr-utilities';
import {
	NavigationSearchDialog,
	QuickFindNav3,
	useQuickFindEnabled,
} from '@confluence/navigation-search-dialog';
import { useRouteDataRef } from '@confluence/route-manager';
import { START_TOUCH } from '@confluence/navdex';
import { AppNavigationContext } from '@confluence/app-navigation-context';
import { CONTEXT_PATH } from '@confluence/named-routes/src';
import { useSSRPlaceholderReplaceIdProp } from '@confluence/loadable';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledContainer = styled.div({
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	position: 'relative',
});

type SearchComponentProps = {
	theme?: SearchCSS;
};

export const SearchComponent: FC<SearchComponentProps> = ({ theme }) => {
	const ssrPlaceholderIdProp = useSSRPlaceholderReplaceIdProp();
	const routeDataRef = useRouteDataRef();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { resetStickySearchRef } = useContext(AppNavigationContext);
	const renderServerPlaceholder = useRenderServerPlaceholder();
	const [isQuickFindEnabled] = useQuickFindEnabled();

	const fireAnalytics = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'globalNavigation',
				action: 'clicked',
				actionSubject: 'navigationItem',
				actionSubjectId: 'quickSearch',
				attributes: {
					selectedItemPageContext: routeDataRef.current?.routeName,
					navigationLayer: 'global',
					navVersion: '3',
					navdexPointType: START_TOUCH,
				},
			},
		}).fire();
	}, [createAnalyticsEvent, routeDataRef]);

	const searchComponent = useMemo(() => {
		/* If this user has Quick Find, always render the Quick Find regardless
		 * of whether it's client or server side. We must do this check at this
		 * level as the Quick Search component is not SSR compatible, whilst the
		 * Quick Find component is.
		 */
		if (isQuickFindEnabled) {
			return <QuickFindNav3 />;
		}

		/**
		 * Both Quick Find and Quick Search are compatible with the SSR placeholder.
		 * There is logic within NavigationSearchDialog which determines whether
		 * Quick Find or Quick Search is rendered.
		 */
		if (renderServerPlaceholder) {
			return (
				<ProductSearchInputSkeletonInteractiveSSR
					placeholder="Search"
					advancedSearchUrl={`${CONTEXT_PATH}/search`}
					shouldFillContainer
				/>
			);
		} else {
			return <NavigationSearchDialog theme={theme} clearStickySearchRef={resetStickySearchRef} />;
		}
	}, [isQuickFindEnabled, renderServerPlaceholder, resetStickySearchRef, theme]);

	return (
		<StyledContainer
			data-testid="app-navigation-search"
			data-vc="app-navigation-search"
			{...ssrPlaceholderIdProp}
			onClick={fireAnalytics}
		>
			{searchComponent}
		</StyledContainer>
	);
};
